import { useState, useEffect, useCallback } from 'react';
import { History, ChevronDown, ChevronRight, Loader2, User } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { toastMessage } from '@/utils/toast-message';

const BASE = '/api/parent-app/admin';
const LIMIT = 50;

interface ChangeEntry {
  id: number;
  entity_type: string;
  entity_id: string;
  action: string;
  changes: { field: string; old_value: any; new_value: any }[] | null;
  changed_by_id: number;
  changed_by_name: string;
  changed_by_role: string;
  ip_address: string | null;
  changed_at: string;
}

const ENTITY_LABELS: Record<string, string> = {
  student:         'Student',
  student_contact: 'Contact',
  student_medical: 'Medical',
  homework:        'Homework',
  event:           'Event',
  complaint:       'Complaint',
  leave_request:   'Leave',
  teacher:         'Teacher',
  class:           'Class',
  fee_structure:   'Fee Structure',
  settings:        'Settings',
  profile:         'Profile',
};

const ACTION_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  create:        { label: 'Created',       color: '#22c55e', bg: '#22c55e18' },
  update:        { label: 'Updated',       color: '#3b82f6', bg: '#3b82f618' },
  delete:        { label: 'Deleted',       color: '#ef4444', bg: '#ef444418' },
  status_change: { label: 'Status Change', color: '#f59e0b', bg: '#f59e0b18' },
  bulk_update:   { label: 'Bulk Update',   color: '#8b5cf6', bg: '#8b5cf618' },
};

const ROLE_LABELS: Record<string, string> = {
  school_admin: 'Admin',
  teacher:      'Teacher',
  parent:       'Parent',
};

const ENTITY_TYPES = Object.entries(ENTITY_LABELS) as [string, string][];
const ACTIONS      = Object.entries(ACTION_CONFIG) as [string, { label: string; color: string; bg: string }][];

function fmt(d: string) {
  return new Date(d).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function fmtValue(v: any) {
  if (v === null || v === undefined) return <span style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>—</span>;
  if (typeof v === 'boolean') return <span>{v ? 'Yes' : 'No'}</span>;
  return <span>{String(v)}</span>;
}

function ChangeRow({ entry }: { entry: ChangeEntry }) {
  const [expanded, setExpanded] = useState(false);
  const action  = ACTION_CONFIG[entry.action] ?? { label: entry.action, color: '#94a3b8', bg: '#94a3b818' };
  const changes = entry.changes
    ? (typeof entry.changes === 'string' ? JSON.parse(entry.changes) : entry.changes)
    : [];
  const hasChanges = changes.length > 0;

  // flexShrink: 0 below is load-bearing: this is a flex item in a column flex
  // container with overflowY: auto. overflow: hidden makes its automatic
  // min-height 0 per the flexbox spec, so without flexShrink: 0 the browser
  // squeezes every row down to a sliver instead of letting the list scroll
  // once 50 rows don't fit the visible area.
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', flexShrink: 0 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: hasChanges ? 'pointer' : 'default' }}
        onClick={() => hasChanges && setExpanded(e => !e)}
      >
        {/* Expand toggle */}
        <div style={{ width: 18, flexShrink: 0, color: 'var(--text-3)' }}>
          {hasChanges
            ? (expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)
            : <span style={{ width: 14, display: 'inline-block' }} />
          }
        </div>

        {/* Entity type + ID */}
        <div style={{ minWidth: 120, flexShrink: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
            {ENTITY_LABELS[entry.entity_type] ?? entry.entity_type}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'monospace' }}>
            #{entry.entity_id}
          </div>
        </div>

        {/* Action badge */}
        <div style={{ flexShrink: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: action.bg, color: action.color, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {action.label}
          </span>
        </div>

        {/* Who */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flexShrink: 0, width: 28, height: 28, borderRadius: '50%', background: 'var(--accent-light, #e0e7ff)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <User size={14} style={{ color: 'var(--accent-deep, #4f46e5)' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {entry.changed_by_name || `User #${entry.changed_by_id}`}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
              {ROLE_LABELS[entry.changed_by_role] ?? entry.changed_by_role}
            </div>
          </div>
        </div>

        {/* Fields count */}
        {hasChanges && (
          <div style={{ flexShrink: 0, fontSize: 11, color: 'var(--text-3)', marginRight: 8 }}>
            {changes.length} field{changes.length !== 1 ? 's' : ''}
          </div>
        )}

        {/* When */}
        <div style={{ flexShrink: 0, textAlign: 'right' }}>
          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{fmt(entry.changed_at)}</div>
          {entry.ip_address && (
            <div style={{ fontSize: 10, color: 'var(--text-3)', fontFamily: 'monospace' }}>{entry.ip_address}</div>
          )}
        </div>
      </div>

      {/* Expanded field diff */}
      {expanded && hasChanges && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '10px 16px 12px 48px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {changes.map((c: any, i: number) => (
            <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12 }}>
              <span style={{ minWidth: 120, fontWeight: 600, color: 'var(--text-2)', textTransform: 'capitalize', flexShrink: 0 }}>
                {c.field.replace(/_/g, ' ')}
              </span>
              <span style={{ color: '#ef4444', textDecoration: 'line-through', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {fmtValue(c.old_value)}
              </span>
              <span style={{ color: 'var(--text-3)', fontSize: 10 }}>→</span>
              <span style={{ color: '#22c55e', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {fmtValue(c.new_value)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ChangeLogsPage() {
  const [items,       setItems]       = useState<ChangeEntry[]>([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [filterType,  setFilterType]  = useState('');
  const [filterAction,setFilterAction]= useState('');

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(pg), limit: String(LIMIT) });
      if (filterType)   qs.set('entity_type', filterType);
      if (filterAction) qs.set('action',       filterAction);
      const res = await apiRequest<{ data: ChangeEntry[]; total: number }>(
        `${BASE}/change-logs?${qs}`
      );
      // apiRequest resolves (doesn't throw) on HTTP error responses, so a
      // failed fetch must be checked explicitly or it silently renders as an
      // empty list instead of an error.
      if (!res.success) { toastMessage(res.message || 'Failed to load change logs', 'error'); setItems([]); setTotal(0); return; }
      setItems(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
      setPage(pg);
    } catch { toastMessage('Failed to load change logs', 'error'); }
    finally { setLoading(false); }
  }, [filterType, filterAction]);

  useEffect(() => { load(1); }, [load]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg)' }}>

      {/* Header */}
      <div className="page-head">
        <div className="page-head-icon"><History className="w-6 h-6" /></div>
        <div className="page-head-text">
          <h1>Activity Log</h1>
          <p>Track all edits and changes made in the parent app</p>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '24px 32px', gap: 16, overflow: 'hidden', minHeight: 0 }}>

        {/* Filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', flexShrink: 0 }}>
          <select className="select" style={{ width: 'auto' }} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All Entities</option>
            {ENTITY_TYPES.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
          </select>

          <select className="select" style={{ width: 'auto' }} value={filterAction} onChange={e => setFilterAction(e.target.value)}>
            <option value="">All Actions</option>
            {ACTIONS.map(([val, cfg]) => <option key={val} value={val}>{cfg.label}</option>)}
          </select>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{total}</span>
            <span className="meta">record{total !== 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 8 }}>
          {loading ? (
            <div className="center" style={{ padding: '64px 20px' }}>
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--accent-deep)' }} />
            </div>
          ) : items.length === 0 ? (
            <div className="empty">
              <div className="empty-art"><History size={32} /></div>
              <h3>No activity yet</h3>
              <p>Changes made in the parent app will appear here.</p>
            </div>
          ) : (
            items.map(entry => <ChangeRow key={entry.id} entry={entry} />)
          )}
        </div>

        {/* Pagination */}
        {total > LIMIT && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, flexShrink: 0 }}>
            <button className="btn btn-secondary" disabled={page === 1} onClick={() => load(page - 1)}>Previous</button>
            <span style={{ fontSize: 13, color: 'var(--text-2)', alignSelf: 'center' }}>
              Page {page} of {Math.ceil(total / LIMIT)}
            </span>
            <button className="btn btn-secondary" disabled={page * LIMIT >= total} onClick={() => load(page + 1)}>Next</button>
          </div>
        )}

      </div>
    </div>
  );
}
